// src/components/ClientPortalDebtRecovery.js

import React, { useState, useCallback } from 'react';
import { formatCurrency } from '../utils/currency';

// Linear status pipeline (order matters)
const STATUS_PIPELINE = [
  { key: 'INTAKE', label: 'Intake' },
  { key: 'DEMAND_SENT', label: 'Demand Sent' },
  { key: 'NEGOTIATION', label: 'Negotiation' },
  { key: 'LITIGATION', label: 'Litigation' },
  { key: 'SETTLEMENT', label: 'Settlement' },
  { key: 'RECOVERED', label: 'Recovered' },
];

const TERMINAL_STATUSES = ['WRITTEN_OFF', 'CLOSED'];
const ACTIVE_EXCLUDE = ['CLOSED', 'WRITTEN_OFF'];

const STATUS_BADGE_TW = {
  INTAKE: 'bg-blue-100 text-blue-800',
  DEMAND_SENT: 'bg-blue-100 text-blue-800',
  NEGOTIATION: 'bg-yellow-100 text-yellow-800',
  LITIGATION: 'bg-red-100 text-red-800',
  SETTLEMENT: 'bg-yellow-100 text-yellow-800',
  PARTIALLY_RECOVERED: 'bg-green-100 text-green-800',
  RECOVERED: 'bg-green-100 text-green-800',
  WRITTEN_OFF: 'bg-gray-200 text-gray-600',
  CLOSED: 'bg-gray-800 text-white',
};

const PAYMENT_METHOD_LABELS = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank Transfer',
  MOBILE_MONEY: 'Mobile Money',
  CHEQUE: 'Cheque',
  OTHER: 'Other',
};

function StatusPipeline({ status }) {
  if (TERMINAL_STATUSES.includes(status)) {
    return (
      <div className="flex items-center gap-2 mt-2">
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGE_TW[status] || 'bg-gray-200 text-gray-600'}`}>
          {status === 'WRITTEN_OFF' ? 'Written Off' : 'Closed'}
        </span>
        <span className="text-sm text-gray-400">This mandate has been {status === 'WRITTEN_OFF' ? 'written off' : 'closed'}.</span>
      </div>
    );
  }

  const effectiveStatus = status === 'PARTIALLY_RECOVERED' ? 'SETTLEMENT' : status;
  const currentIdx = STATUS_PIPELINE.findIndex(s => s.key === effectiveStatus);

  return (
    <div className="flex items-center flex-wrap gap-1 mt-2">
      {STATUS_PIPELINE.map((stage, idx) => {
        let cls = 'bg-gray-100 text-gray-400 border border-gray-200';
        if (idx < currentIdx) cls = 'bg-success text-white';
        else if (idx === currentIdx) cls = 'bg-accent text-white';

        return (
          <React.Fragment key={stage.key}>
            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${cls}`}>
              {stage.label}
            </span>
            {idx < STATUS_PIPELINE.length - 1 && (
              <i className={`bi bi-chevron-right text-[0.65rem] ${idx < currentIdx ? 'text-success' : 'text-gray-300'}`}></i>
            )}
          </React.Fragment>
        );
      })}
      {status === 'PARTIALLY_RECOVERED' && (
        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-success text-white ml-1">
          Partially Recovered
        </span>
      )}
    </div>
  );
}

function ClientPortalDebtRecovery({ currentUser, clients, mandates, communicationLogs, authAxios }) {
  const [expandedMandate, setExpandedMandate] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const toggleExpand = useCallback(async (mandateId) => {
    if (expandedMandate === mandateId) {
      setExpandedMandate(null);
      setPayments([]);
      return;
    }
    setExpandedMandate(mandateId);
    if (!authAxios) return;
    setLoadingPayments(true);
    try {
      const resp = await authAxios.get('debt-recovery-payments/my-payments/', {
        params: { request: mandateId },
      });
      setPayments(resp.data?.results || resp.data || []);
    } catch (err) {
      console.error('Failed to fetch payments', err);
      setPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  }, [expandedMandate, authAxios]);

  const activeMandates = mandates.filter(m => !ACTIVE_EXCLUDE.includes(m.status));
  const totalPrincipal = activeMandates.reduce((sum, m) => sum + parseFloat(m.principal_amount || 0), 0);
  const totalInterest = activeMandates.reduce((sum, m) => sum + parseFloat(m.accumulated_interest || 0), 0);
  const totalClaim = activeMandates.reduce((sum, m) => {
    const claim = m.total_claim_amount != null
      ? parseFloat(m.total_claim_amount)
      : parseFloat(m.principal_amount || 0) + parseFloat(m.accumulated_interest || 0);
    return sum + (Number.isFinite(claim) ? claim : 0);
  }, 0);
  const totalRecovered = activeMandates.reduce((sum, m) => sum + parseFloat(m.total_recovered || 0), 0);
  const totalOutstanding = activeMandates.reduce((sum, m) => sum + parseFloat(m.balance_remaining || 0), 0);

  const mandateLogs = expandedMandate
    ? (communicationLogs || []).filter(log => log.debt_recovery_request === expandedMandate)
    : [];

  const getProgress = (mandate) => {
    const claim = mandate.total_claim_amount != null
      ? parseFloat(mandate.total_claim_amount)
      : parseFloat(mandate.principal_amount || 0) + parseFloat(mandate.accumulated_interest || 0);
    const recovered = parseFloat(mandate.total_recovered || 0);
    if (!Number.isFinite(claim) || claim <= 0) return 0;
    return Math.min(100, Math.round((recovered / claim) * 100));
  };

  if (!mandates || mandates.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">
          <i className="bi bi-cash-coin mr-2"></i>Debt Recovery
        </h2>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <i className="bi bi-info-circle mr-2 text-blue-500"></i>
          <span className="text-blue-700">No debt recovery mandates found for your account.</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-primary mb-4">
        <i className="bi bi-cash-coin mr-2"></i>Debt Recovery
      </h2>

      {/* Summary Banner */}
      {activeMandates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 text-center">
            <div className="text-sm text-gray-400 mb-1">Total Principal</div>
            <div className="text-2xl font-bold text-primary">{formatCurrency(totalPrincipal)}</div>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 text-center">
            <div className="text-sm text-gray-400 mb-1">Total Interest</div>
            <div className="text-2xl font-bold text-primary">{formatCurrency(totalInterest)}</div>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 text-center">
            <div className="text-sm text-gray-400 mb-1">Total Claim</div>
            <div className="text-2xl font-bold text-primary">{formatCurrency(totalClaim)}</div>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 text-center">
            <div className="text-sm text-gray-400 mb-1">Total Recovered</div>
            <div className="text-2xl font-bold text-success">{formatCurrency(totalRecovered)}</div>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 text-center">
            <div className="text-sm text-gray-400 mb-1">Total Outstanding</div>
            <div className="text-2xl font-bold text-error">{formatCurrency(totalOutstanding)}</div>
          </div>
        </div>
      )}

      {/* Mandate Cards */}
      <div className="space-y-4">
        {mandates.map((mandate) => {
          const progress = getProgress(mandate);
          const isExpanded = expandedMandate === mandate.id;
          const badgeClass = STATUS_BADGE_TW[mandate.status] || 'bg-gray-200 text-gray-600';
          const statusLabel = (mandate.status || '').replace(/_/g, ' ');

          return (
            <div key={mandate.id} className="bg-white rounded-lg shadow border border-gray-200">
              <div className="p-5">
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h5 className="text-lg font-semibold mb-1">
                      <span className="text-accent">{mandate.reference}</span>
                      {mandate.title && <span className="text-gray-400 ml-2">— {mandate.title}</span>}
                    </h5>
                    <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                      <span><i className="bi bi-person mr-1"></i> Debtor: <strong className="text-gray-700">{mandate.debtor_name || 'N/A'}</strong></span>
                      {mandate.assigned_to_username && (
                        <span><i className="bi bi-briefcase mr-1"></i> Attorney: <strong className="text-gray-700">{mandate.assigned_to_username}</strong></span>
                      )}
                      {mandate.opened_date && (
                        <span><i className="bi bi-calendar mr-1"></i> Opened: {mandate.opened_date}</span>
                      )}
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${badgeClass}`}>
                    {statusLabel}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Recovered: {formatCurrency(mandate.total_recovered)}</span>
                    <span>
                      Claim: {formatCurrency(
                        mandate.total_claim_amount != null
                          ? mandate.total_claim_amount
                          : (Number(mandate.principal_amount || 0) + Number(mandate.accumulated_interest || 0))
                      )}
                      {Number(mandate.accumulated_interest || 0) > 0 && (
                        <span className="text-gray-300">
                          {' '} (P: {formatCurrency(mandate.principal_amount)} + I: {formatCurrency(mandate.accumulated_interest)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all ${progress >= 100 ? 'bg-success' : 'bg-accent'}`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {progress}% recovered — {formatCurrency(mandate.balance_remaining)} remaining
                  </div>
                </div>

                {/* Expand toggle */}
                <button
                  className="text-sm text-accent hover:text-blue-800 font-medium flex items-center gap-1"
                  onClick={() => toggleExpand(mandate.id)}
                >
                  <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  {isExpanded ? 'Hide Details' : 'View Details'}
                </button>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-6">
                    {/* Status Pipeline */}
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 mb-2">Status Progress</h6>
                      <StatusPipeline status={mandate.status} />
                    </div>

                    {/* Payment History */}
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 mb-2">Payment History</h6>
                      {loadingPayments ? (
                        <div className="text-center py-4 text-gray-400">Loading payments...</div>
                      ) : payments.length === 0 ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-500">
                          <i className="bi bi-info-circle mr-2"></i>No payments recorded yet.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Amount</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Method</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Reference</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {payments.map((p) => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-600">{p.payment_date}</td>
                                  <td className="px-3 py-2 text-success font-semibold">{formatCurrency(p.amount)}</td>
                                  <td className="px-3 py-2 text-gray-600">{PAYMENT_METHOD_LABELS[p.method] || p.method}</td>
                                  <td className="px-3 py-2 text-gray-600">{p.reference || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Communication Log */}
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 mb-2">Communication Log</h6>
                      {mandateLogs.length === 0 ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-500">
                          <i className="bi bi-info-circle mr-2"></i>No communication entries for this mandate.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Type</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600">Subject</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {mandateLogs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-600">{log.timestamp ? new Date(log.timestamp).toLocaleDateString() : '—'}</td>
                                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{log.log_type || '—'}</span></td>
                                  <td className="px-3 py-2 text-gray-600">{log.subject || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ClientPortalDebtRecovery;
